import { StringHelper } from "@octopusdeploy/design-system-octopus-components";
import type { FieldErrors } from "@octopusdeploy/octopus-react-client";
import type { ArgoCDApplicationResource, ArgoCDApplicationSourceResource, ArgoCDGatewayResource, ArgoCDInstanceSummaryResource, Repository } from "@octopusdeploy/octopus-server-client";
import { ArgoCDAnnotationKeys } from "@octopusdeploy/octopus-server-client";
import type { JwtPayload } from "jwt-decode";
import { jwtDecode } from "jwt-decode";
import moment from "moment";
import URI from "urijs";
import type { ArgoCDRegistrationPageEvent } from "~/analytics/Analytics";
import type { ArgoCDInstanceConfiguration } from "~/areas/infrastructure/components/ArgoCDInstances/ArgoCDInstanceConfiguration";
import { ConfigurationMessages } from "~/areas/infrastructure/components/ArgoCDInstances/DialogViews/ConfigurationMessages";
import { getExpiry } from "~/areas/infrastructure/components/ArgoCDInstances/useBearerToken";
import { defaultArgoDNSUrl } from "./AddArgoCDInstanceDialog";

type ArgoCDGateway = ArgoCDGatewayResource | ArgoCDInstanceSummaryResource;

export async function validateConfiguration(repo: Repository, modelToValidate: ArgoCDInstanceConfiguration): Promise<FieldErrors> {
    return {
        ...(await validateField("name", () => validateName(repo, modelToValidate.name))),
        ...(await validateField("argoCDUrl", () => validateArgoCDUrl(modelToValidate.argoCDUrl))),
        ...(await validateField("argoCDWebUrl", () => validateArgoCDWebUiUrl(modelToValidate.argoCDWebUrl))),
        ...(await validateField("argoCDAuthenticationToken", () => validateArgoCDAuthToken(modelToValidate.argoCDAuthenticationToken))),
        ...(await validateField("octopusServerUrl", () => validateOctopusServerUrl(modelToValidate.octopusServerUrl))),
        ...(await validateField("octopusGrpcUrl", () => validateOctopusServerGrpcUrl(modelToValidate.octopusGrpcUrl))),
    };
}

async function validateField(field: keyof ArgoCDInstanceConfiguration, validateAction: () => string | undefined | Promise<string | undefined>): Promise<Partial<Record<keyof ArgoCDInstanceConfiguration, string>>> {
    const error = await validateAction();
    return error
        ? {
              [field]: error,
          }
        : {};
}

async function validateName(repo: Repository, name: string): Promise<string | undefined> {
    if (StringHelper.isNullOrWhiteSpace(name)) {
        return ConfigurationMessages.Name.Validation.NotProvided;
    } else {
        try {
            const existingGateways = await repo.ArgoCDInstances.list({ name });
            if (existingGateways.Resources.length > 0) {
                return ConfigurationMessages.Name.Validation.AlreadyExists;
            }
        } catch {
            return ConfigurationMessages.Name.Validation.UnableToValidate;
        }
    }
}

function validateArgoCDUrl(url: string): string | undefined {
    if (StringHelper.isNullOrWhiteSpace(url)) {
        return ConfigurationMessages.ArgoCDUri.Validation.NotProvided;
    }
}

function validateArgoCDWebUiUrl(url: string): string | undefined {
    // If it's null, undefined or empty we don't want to validate
    if (!url) {
        return;
    }

    if (!isValidUrl(url)) {
        return ConfigurationMessages.ArgoCDWebUiUri.Validation.Invalid;
    }
}

function validateArgoCDAuthToken(token: string): string | undefined {
    if (StringHelper.isNullOrWhiteSpace(token)) {
        return ConfigurationMessages.ArgoCDAuthToken.Validation.NotProvided;
    }

    //try and decode the token into a valid JWT, if it fails, fail validation
    try {
        const jwt = jwtDecode<JwtPayload>(token);

        const expiry = getExpiry(jwt);
        //if there is an expiry, and it's less than now, we must have an expired token
        if (expiry && expiry < moment()) {
            return ConfigurationMessages.ArgoCDAuthToken.Validation.Expired;
        }
    } catch {
        return ConfigurationMessages.ArgoCDAuthToken.Validation.Invalid;
    }
}

function validateOctopusServerUrl(url: string): string | undefined {
    if (StringHelper.isNullOrWhiteSpace(url)) {
        return ConfigurationMessages.ServerUri.Validation.NotProvided;
    } else if (!isValidUrl(url)) {
        return ConfigurationMessages.ServerUri.Validation.Invalid;
    }
}

function validateOctopusServerGrpcUrl(url: string): string | undefined {
    if (StringHelper.isNullOrWhiteSpace(url)) {
        return ConfigurationMessages.ServerGrpcUri.Validation.NotProvided;
    } else if (!isValidUrl(url)) {
        return ConfigurationMessages.ServerGrpcUri.Validation.Invalid;
    }
}

function isValidUrl(url: string): boolean {
    try {
        const uri = new URI(url);
        return uri.is("absolute") && uri.is("url") && (uri.is("name") || uri.is("ip"));
    } catch (error) {
        return false;
    }
}

function existsAndIsValid(value: string, validation: (value: string) => string | undefined): boolean | "Invalid" {
    if (!value) {
        return false;
    }

    if (!validation(value)) {
        return true;
    }

    return "Invalid";
}

export function configurationToRegistrationEvent(configuration: ArgoCDInstanceConfiguration): ArgoCDRegistrationPageEvent {
    return {
        gatewayName: configuration.name,
        spaceId: configuration.spaceId,
        gatewayNamePopulated: !!configuration.name,
        environmentsCount: configuration.environmentIds.length,
        webInterfaceUrlPopulated: existsAndIsValid(configuration.argoCDWebUrl, validateArgoCDUrl),
        argoCDServiceDnsPopulated: configuration.argoCDUrl == defaultArgoDNSUrl ? "Default Value" : !!configuration.argoCDUrl,
        argoCDAuthTokenPopulated: existsAndIsValid(configuration.argoCDAuthenticationToken, validateArgoCDAuthToken),
        argoCDInstanceUsesSelfSignedCertificate: configuration.argoAllowUnsignedCertificate,
        octopusServerUrlPopulated: existsAndIsValid(configuration.octopusServerUrl, validateOctopusServerUrl),
        octopusServerGrpcUrlPopulated: existsAndIsValid(configuration.octopusGrpcUrl, validateOctopusServerGrpcUrl),
    };
}

function getSourcesWithRequiringGitCredentials(application: ArgoCDApplicationResource) {
    return application.Sources.filter((src) => src.RequiresGitCredential);
}

export function hasSourcesWithoutMatchedGitCredentials(application: ArgoCDApplicationResource) {
    //we don't require git credentials
    return getSourcesWithRequiringGitCredentials(application).some((src) => !src.BestMatchedGitCredential);
}

export function isSourceMissingRequireAnnotations(source: ArgoCDApplicationSourceResource, application: ArgoCDApplicationResource): boolean {
    const containsMultipleSources = application.Sources.length > 1;

    const missingProject = getProjectAnnotation(containsMultipleSources, source, application.Annotations) === undefined;
    const missingEnvironment = getEnvironmentAnnotation(containsMultipleSources, source, application.Annotations) === undefined;

    //If the source isn't scoped at all (not relevant)
    if (missingProject && missingEnvironment) {
        return false;
    }

    return missingProject || missingEnvironment;
}

export function isMissingRequiredAnnotations(application: ArgoCDApplicationResource) {
    return application.Sources.some((source) => isSourceMissingRequireAnnotations(source, application));
}

export function isApplicationMissingRequiredConfiguration(application: ArgoCDApplicationResource, gateway?: ArgoCDGateway): boolean {
    const annotationIssue = isMissingRequiredAnnotations(application);
    const hasCredentialIssue = hasSourcesWithoutMatchedGitCredentials(application);
    const hasEnvironmentScopeIssue = gateway?.EnvironmentIds ? isEnvironmentOutsideGatewayScope(application, gateway.EnvironmentIds) : false;

    return annotationIssue || hasCredentialIssue || hasEnvironmentScopeIssue;
}

export function isEnvironmentOutsideGatewayScope(application: ArgoCDApplicationResource, gatewayEnvironmentIds: string[]): boolean {
    const environmentIssues = getEnvironmentScopeIssues(application, gatewayEnvironmentIds);
    return environmentIssues.length > 0;
}

export function getEnvironmentScopeIssues(application: ArgoCDApplicationResource, gatewayEnvironmentIds: string[]): Array<{ sourceIndex: number }> {
    if (gatewayEnvironmentIds.length === 0) {
        return [];
    }

    return application.Sources.map((source) => {
        if (source.EnvironmentId && !gatewayEnvironmentIds.includes(source.EnvironmentId)) {
            return { sourceIndex: source.Index };
        }
        return undefined;
    }).filter((issue): issue is { sourceIndex: number } => issue !== undefined);
}

function getProjectAnnotation(containsMultipleSources: boolean, source: ArgoCDApplicationSourceResource, annotations: Partial<Record<string, string>>) {
    if (containsMultipleSources && !source.Name) {
        return undefined;
    }

    return annotations[ArgoCDAnnotationKeys.OctopusProjectAnnotationKey(source.Name)];
}

function getEnvironmentAnnotation(containsMultipleSources: boolean, source: ArgoCDApplicationSourceResource, annotations: Partial<Record<string, string>>) {
    if (containsMultipleSources && !source.Name) {
        return undefined;
    }

    return annotations[ArgoCDAnnotationKeys.OctopusEnvironmentAnnotationKey(source.Name)];
}
